import axios from 'axios';
import { mapLocaleToHeaders } from '../services/helpers';
import { userRequestHeaders } from '../services/user';
import useI18n from "./useI18n";

const useApi = () => {
    const { getLocale } = useI18n();

    const apiClient = axios.create({
        baseURL: process.env.REACT_APP_API_URL
    });

    /**
     * Fetches JSON data from the given URL
     *
     * @param {string} url - The URL to fetch data from
     * @returns {Promise<any>} - The JSON response data
     */
    const getJson = async (url) => {    
        const headers = userRequestHeaders('application/json');
        headers['Accept-Language'] = mapLocaleToHeaders(getLocale());
        try {
            const response = await apiClient.get(url, { headers, responseType: 'json' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    /**
     * Fetches binary file data from the given URL
     *
     * @param {string} url - The URL to fetch data from
     * @param {string} acceptedMimeType - The MIME type to accept
     * @returns {Promise<any>} - The JSON response data
     */
    const getBlob = async (url, acceptedMimeType) => {
        const headers = userRequestHeaders('application/json');
        headers['Accept'] = acceptedMimeType;
        try {
            const response = await apiClient.get(url, { headers, responseType: 'blob' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    /**
     * Posts JSON data to the given URL
     *
     * @param {string} url
     * @param {string} data
     * @returns {Promise<any|null>}
     */
    const postJson = async (url, data) => {
        const headers = userRequestHeaders('application/json');
        try {
            const response = await apiClient.post(url, data, { headers, responseType: 'json' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    /**
     * Puts JSON data to the given URL
     *
     * @param {string} url
     * @param {string} data
     * @returns {Promise<any|null>}
     */
    const putJson = async (url, data) => {
        const headers = userRequestHeaders('application/json');
        try {
            const response = await apiClient.put(url, data, { headers, responseType: 'json' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    /**
     * Patches JSON data to the given URL
     *
     * @param {string} url
     * @param {string} data
     * @returns {Promise<any|null>}
     */
    const patchJson = async (url, data) => {
        const headers = userRequestHeaders('application/json');
        try {
            const response = await apiClient.patch(url, data, { headers, responseType: 'json' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    /**
     * Delete resource at the given URL
     *
     * @param {string} url
     * @returns {Promise<any|null>}
     */
    const deleteJson = async (url) => {
        const headers = userRequestHeaders('application/json');
        try {
            const response = await apiClient.delete(url, { headers, responseType: 'json' });
            return response.data;
        } catch (error) {
            processError(error);
            return null;
        }
    }

    const processError = (error) => {
        if (error.status === 401) {
            // Handle unauthorized access by redirecting to the logout page (will clear the session)
            window.location.href = '/#/logout?alert=authError';
        } else {
            // Handle other API errors by passing the error forward
            throw error;
        }
    }

    return {
        getJson,
        getBlob,
        postJson,
        putJson,
        patchJson,
        deleteJson
    }
}

export default useApi
